import { ContentPanel } from '@/components/content-panel';
import { useLayoutContext } from '@/contexts';
import { useFacility } from '@/contexts/facility-context/hook';
import { PrimaryLayout } from '@/layouts/primary';
import { Flex, Grid, Select, Text, Title } from '@mantine/core';
import {
	Icon,
	IconLayout2,
	IconLayoutGridAdd,
	IconPlus,
	IconProps,
	IconTruckLoading,
} from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import styles from './styles.module.css';

type PossibleScanTypes =
	| 'BAG_SCAN'
	| 'BALE_CREATE'
	| 'BALE_OUTBOUND'
	| 'BALE_INBOUND'
	| 'BALE_PROCESSED'
	| 'BAG_AUDIT';

type FacilityActionsObject = {
	bag?: Array<{
		action: 'BAG_SCAN' | 'BAG_AUDIT';
		label: string;
		icon: React.ForwardRefExoticComponent<IconProps & React.RefAttributes<Icon>>;
		href: string;
	}>;
	bale?: Array<{
		action: 'BALE_CREATE' | 'BALE_OUTBOUND' | 'BALE_INBOUND' | 'BALE_PROCESSED';
		label: string;
		icon: React.ForwardRefExoticComponent<IconProps & React.RefAttributes<Icon>>;
		href: string;
	}>;
};

export function FacilityHomePage() {
	const navigate = useNavigate();
	const { currentFacility } = useFacility();

	const availableActions = useMemo(() => {
		if (!currentFacility || !currentFacility.scanTypes) return {};
		const scanTypes = currentFacility.scanTypes.map((st) => st.name) as PossibleScanTypes[];
		const actions: FacilityActionsObject = {};
		for (const type of scanTypes) {
			switch (type) {
				case 'BAG_SCAN':
					{
						const action = {
							action: 'BAG_SCAN' as const,
							label: 'Scan Bags',
							icon: IconLayoutGridAdd,
							href: '/facility/bag-scan',
						};
						if (!actions.bag) {
							actions.bag = [action];
						} else {
							actions.bag.push(action);
						}
					}
					break;
				case 'BAG_AUDIT':
					{
						const action = {
							action: 'BAG_AUDIT' as const,
							label: 'Audit Bags',
							icon: IconLayoutGridAdd,
							href: '/facility/bag-audit',
						};
						if (!actions.bag) {
							actions.bag = [action];
						} else {
							actions.bag.push(action);
						}
					}
					break;
				case 'BALE_CREATE':
					{
						const action = {
							action: 'BALE_CREATE' as const,
							label: 'Create Bale',
							icon: IconPlus,
							href: '/facility/bale-create',
						};
						if (!actions.bale) {
							actions.bale = [action];
						} else {
							actions.bale.push(action);
						}
					}
					break;
				case 'BALE_OUTBOUND':
					{
						const action = {
							action: 'BALE_OUTBOUND' as const,
							label: 'Outbound Bale',
							icon: IconTruckLoading,
							href: '/facility/bale-outbound',
						};
						if (!actions.bale) {
							actions.bale = [action];
						} else {
							actions.bale.push(action);
						}
					}
					break;
				case 'BALE_INBOUND':
					{
						const action = {
							action: 'BALE_INBOUND' as const,
							label: 'Inbound Bale',
							icon: IconLayout2,
							href: '/facility/bale-inbound',
						};
						if (!actions.bale) {
							actions.bale = [action];
						} else {
							actions.bale.push(action);
						}
					}
					break;
				case 'BALE_PROCESSED':
					{
						const action = {
							action: 'BALE_PROCESSED' as const,
							label: 'Processed Bale',
							icon: IconLayout2,
							href: '/facility/bale-processed',
						};
						if (!actions.bale) {
							actions.bale = [action];
						} else {
							actions.bale.push(action);
						}
					}
					break;
			}
		}
		return actions;
	}, [currentFacility]);

	const handleActionClick = (href: string) => {
		navigate({ to: href });
	};

	const { isDesktop } = useLayoutContext();

	return (
		<PrimaryLayout header={{ showLogo: true, children: <FacilitySelector /> }} context="facility">
			<ContentPanel noPad>
				<Flex direction="column" w="100%" className={styles.actionsContainer}>
					{isDesktop && (
						<Flex
							p="md"
							my="lg"
							align="center"
							gap="sm"
							className={styles.facilitySelectorContainer}
						>
							<Text fw="bold" miw="150px">
								Current Facility:
							</Text>
							<FacilitySelector />
						</Flex>
					)}
					{'bag' in availableActions && (
						<Flex direction="column" className={styles.sectionContainer}>
							<Title order={2} c="dark" className={styles.sectionTitle}>
								Bag Actions
							</Title>
							<Grid w="100%" gutter="sm" className={styles.actionsGrid}>
								{availableActions.bag?.map((action) => (
									<Grid.Col span={6} key={action.action}>
										<Flex
											align="center"
											justify="center"
											direction="column"
											bg="white"
											w="100%"
											className={styles.actionButton}
											onClick={() => handleActionClick(action.href)}
										>
											<Flex
												align="center"
												justify="center"
												bg="blue.3"
												className={styles.iconBackdrop}
											>
												<action.icon size={24} color="#51cf66" />
											</Flex>
											<Text fw={600} c="dark" size="sm" ta="center">
												{action.label}
											</Text>
										</Flex>
									</Grid.Col>
								))}
							</Grid>
						</Flex>
					)}

					{'bale' in availableActions && (
						<Flex direction="column" className={styles.sectionContainer}>
							<Title order={2} c="dark" className={styles.sectionTitle}>
								Bale Actions
							</Title>
							<Grid w="100%" gutter="sm" className={styles.actionsGrid}>
								{availableActions.bale?.map((action) => (
									<Grid.Col span={6} key={action.action}>
										<Flex
											align="center"
											justify="center"
											direction="column"
											bg="white"
											w="100%"
											className={styles.actionButton}
											onClick={() => handleActionClick(action.href)}
										>
											<Flex
												align="center"
												justify="center"
												bg="blue.3"
												className={styles.iconBackdrop}
											>
												<action.icon size={24} color="#51cf66" />
											</Flex>
											<Text fw={600} c="dark" size="sm" ta="center">
												{action.label}
											</Text>
										</Flex>
									</Grid.Col>
								))}
							</Grid>
						</Flex>
					)}
				</Flex>
			</ContentPanel>
		</PrimaryLayout>
	);
}

const FacilitySelector = () => {
	const { currentFacility, selectFacility, availableFacilities } = useFacility();

	return (
		<Flex w="100%" pl="sm" justify="flex-end">
			<Select
				placeholder="Select Facility"
				value={currentFacility?.id ?? ''}
				data={
					availableFacilities?.map((facility) => ({
						label: facility?.name ?? '',
						value: facility?.id ?? '',
					})) ?? []
				}
				onChange={(value) => {
					if (value) selectFacility(value);
				}}
			/>
		</Flex>
	);
};
